/**
 * NoteTemplatesAdmin — Admin > Patients/Leads > Patient Notes: the corporate
 * dashboard's Patient Notes page (notes and their categories) for one
 * account, as a drag-and-drop board like SMS / Email Templates (CategoryBoard):
 *   - categories A→Z, each holding its notes A→Z; drag a note onto another
 *     category to move it there (saved on drop: the note's category changes;
 *     2026-10-07, user: the boards file, they don't order);
 *   - "Add category" top right; on a category row: Add note, Edit (name and
 *     corp's Contains Labs), Delete — only once it holds no live notes — and,
 *     under Show deleted, Restore;
 *   - columns Name and Subject, and corp's content icon;
 *   - a note opens corp's Create / Edit dialog (PatientNoteFormModal);
 *   - search (name and subject) and Show deleted. A note in a deleted
 *     category shows only under Show deleted, with corp's "Category Deleted"
 *     badge; one with no category is listed in Uncategorized and can be
 *     dragged into a category.
 * Corp's multi-account tools (compare, clone, account pickers) are left out.
 *
 * On a Locked page the shared blocks drop Add category, the row icons and
 * drag (AdminBoardPage, AdminRowAction, CategoryBoard); a note still opens
 * read-only (AdminFormModal).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileText, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { AdminBoardPage, AdminListEmpty, AdminListToolbar } from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { CategoryBoard, boardDragHint, type BoardCategory } from '@/components/admin/CategoryBoard';
import { compareTitles, isSeparatorTitle } from '@/components/admin/categoryBoardState';
import { OutlineBadge } from '@/components/admin/CorpBadges';
import { DialogField, DialogFooterLayout, DialogHeading, fieldClasses, type FieldState } from '@/components/admin/DialogChrome';
import { PatientNoteFormModal } from '@/components/admin/PatientNoteFormModal';
import { getErrorMessage } from '@/services/apiClient';
import {
  NOTE_CATEGORY_LAB_OPTIONS,
  createNoteCategory,
  deleteNoteCategory,
  deletePatientNoteTemplate,
  listNoteCategories,
  listPatientNoteTemplates,
  restoreNoteCategory,
  restorePatientNoteTemplate,
  updateNoteCategory,
  updatePatientNoteTemplate,
  type NoteCategory,
  type NoteCategoryLabs,
  type PatientNoteTemplate,
} from '@/services/patientNotesAdmin.service';
import { toast } from '@/store/toastStore';

const SUBTITLE = 'Patient note templates grouped by category.';
/** Added to the subtitle while notes can be dragged (not on a Locked page). */
const DRAG_SUBTITLE = 'Drag a note onto another category to move it there.';

function noteName(n: PatientNoteTemplate): string {
  return n.name.trim() || `Note #${n.id}`;
}

const getRecordId = (n: PatientNoteTemplate) => n.id;
const getRecordTitle = (n: PatientNoteTemplate) => n.name;
const getRecordCategoryId = (n: PatientNoteTemplate) => n.categoryId;
const isDeleted = (n: PatientNoteTemplate) => !n.active;

type FormState = { note: PatientNoteTemplate | null; category: { id: number; name: string } | null } | null;
type CategoryModalState = { current: NoteCategory | null } | null;

// ─── Category modal ───────────────────────────────────────────────────────────

interface NoteCategoryModalProps {
  /** Null to add one. */
  current: NoteCategory | null;
  /** The account's live categories (the duplicate check skips `current`). */
  categories: NoteCategory[];
  onSave: (name: string, isLab: NoteCategoryLabs) => Promise<void>;
  onClose: () => void;
}

/** CategoryNameModal's layout plus corp's Contains Labs. */
function NoteCategoryModal({ current, categories, onSave, onClose }: NoteCategoryModalProps) {
  const isNew = current === null;
  const [name, setName] = useState(current?.name ?? '');
  const [isLab, setIsLab] = useState<NoteCategoryLabs>(current?.isLab ?? 0);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const blank = trimmed === '';
  const taken =
    !blank && categories.some((c) => c.active && c.id !== current?.id && c.name.trim().toLowerCase() === trimmed.toLowerCase());
  const state: FieldState = blank || taken || error ? 'error' : 'none';
  const errors = blank ? ['Category name is required'] : taken ? ['Category name must be unique'] : [];

  async function handleSave() {
    if (blank || taken || saving) return;
    setSaving(true);
    try {
      await onSave(trimmed, isLab);
    } catch (err) {
      setError(getErrorMessage(err, 'The category could not be saved.'));
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={
        <DialogHeading
          title={isNew ? 'Add Note Category' : 'Update Note Category'}
          variant={isNew ? 'default' : 'edit'}
          description={isNew ? 'Add a category for patient notes.' : 'Edit the category name and lab setting.'}
        />
      }
      footer={
        <DialogFooterLayout errors={errors} isCreating={isNew}>
          <div className="flex-1" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={blank || taken}>
            {isNew ? 'Add' : 'Update'}
          </Button>
        </DialogFooterLayout>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
      >
        <DialogField
          label="Category Name"
          htmlFor="note-category-name"
          required
          state={state}
          labelHint={blank ? '— required' : taken ? '— name already used' : undefined}
        >
          <Input
            id="note-category-name"
            autoFocus
            placeholder="Category name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(undefined);
            }}
            error={error}
            className={fieldClasses.input(state)}
          />
        </DialogField>
        <DialogField
          label="Contains Labs"
          htmlFor="note-category-labs"
          description="Marks the category as holding lab results or lab orders, which is how the lab pages find the documents filed under it."
        >
          <Select<NoteCategoryLabs>
            id="note-category-labs"
            options={NOTE_CATEGORY_LAB_OPTIONS}
            value={isLab}
            onChange={(v) => setIsLab(v ?? 0)}
            wrapperClassName="w-fit"
          />
        </DialogField>
      </form>
    </Modal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function NoteTemplatesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [notes, setNotes] = useState<PatientNoteTemplate[]>([]);
  const [categories, setCategories] = useState<NoteCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [formState, setFormState] = useState<FormState>(null);
  const [categoryModal, setCategoryModal] = useState<CategoryModalState>(null);
  const [deletingNote, setDeletingNote] = useState<PatientNoteTemplate | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<BoardCategory | null>(null);

  // Only the latest load may write.
  const loadSeq = useRef(0);
  const load = useCallback(async (quiet = false) => {
    const seq = ++loadSeq.current;
    if (!quiet) setLoading(true);
    try {
      const [list, cats] = await Promise.all([listPatientNoteTemplates(), listNoteCategories()]);
      if (seq !== loadSeq.current) return;
      setNotes(list);
      setCategories(cats);
    } catch (err) {
      if (seq === loadSeq.current) toast.error(getErrorMessage(err, 'Failed to load patient notes'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Board data ──────────────────────────────────────────────────────────────

  const liveCategories = useMemo(() => categories.filter((c) => c.active), [categories]);
  const liveCategoryIds = useMemo(() => new Set(liveCategories.map((c) => c.id)), [liveCategories]);
  const deletedCategoryIds = useMemo(() => new Set(categories.filter((c) => !c.active).map((c) => c.id)), [categories]);

  const allBoardCategories = useMemo(
    (): BoardCategory[] =>
      categories
        .filter((c) => (showDeleted || c.active) && !isSeparatorTitle(c.name))
        .sort((a, b) => compareTitles(a.name, b.name))
        .map((c) => ({ id: c.id, title: c.name, deleted: !c.active })),
    [categories, showDeleted],
  );

  /** A note filed under a deleted category: shown (as corp) only under Show deleted. */
  const inDeletedCategory = useCallback(
    (n: PatientNoteTemplate) => n.categoryId != null && deletedCategoryIds.has(n.categoryId) && !liveCategoryIds.has(n.categoryId),
    [deletedCategoryIds, liveCategoryIds],
  );

  const listed = useMemo(
    () => notes.filter((n) => showDeleted || (n.active && !inDeletedCategory(n))),
    [notes, showDeleted, inDeletedCategory],
  );

  const query = search.trim().toLowerCase();
  const searching = query !== '';
  const records = useMemo(
    () =>
      searching
        ? listed.filter((n) => n.name.toLowerCase().includes(query) || n.subject.toLowerCase().includes(query))
        : listed,
    [listed, searching, query],
  );
  const boardCategories = useMemo(() => {
    if (!searching) return allBoardCategories;
    const withMatches = new Set(records.map((n) => n.categoryId));
    return allBoardCategories.filter((c) => withMatches.has(c.id));
  }, [allBoardCategories, records, searching]);

  /** Live notes filed under a category — a category is deleted only once it has none. */
  const liveIn = useCallback((categoryId: number) => notes.filter((n) => n.active && n.categoryId === categoryId).length, [notes]);

  // ── Saves ───────────────────────────────────────────────────────────────────

  /** A drop: notes have no saved order, so only the moved note (the one not yet in this category) is saved. */
  const handleMove = useCallback(
    async (categoryId: number, ids: number[]) => {
      const moved = notes.filter((n) => ids.includes(n.id) && n.categoryId !== categoryId);
      await Promise.all(moved.map((n) => updatePatientNoteTemplate(n.id, { categoryId })));
      const movedIds = new Set(moved.map((n) => n.id));
      setNotes((prev) => prev.map((n) => (movedIds.has(n.id) ? { ...n, categoryId } : n)));
    },
    [notes],
  );

  const handleSaveError = useCallback((err: unknown) => {
    toast.error(getErrorMessage(err, 'The note could not be moved.'));
  }, []);

  function handleNoteSaved(saved: PatientNoteTemplate | null, isNew: boolean) {
    setFormState(null);
    if (!saved) {
      void load(true);
      return;
    }
    setNotes((prev) => (isNew ? [...prev, saved] : prev.map((n) => (n.id === saved.id ? { ...n, ...saved } : n))));
  }

  async function handleDeleteNote() {
    if (!deletingNote) return;
    const id = deletingNote.id;
    try {
      await deletePatientNoteTemplate(id);
      setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, active: false } : n)));
      toast.success(`"${noteName(deletingNote)}" deleted`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Delete failed'));
    }
  }

  async function handleRestoreNote(note: PatientNoteTemplate) {
    try {
      await restorePatientNoteTemplate(note.id);
      setNotes((prev) => prev.map((n) => (n.id === note.id ? { ...n, active: true } : n)));
      toast.success(`${noteName(note)} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Restore failed'));
    }
  }

  async function handleCategorySave(name: string, isLab: NoteCategoryLabs) {
    const current = categoryModal?.current ?? null;
    if (current) {
      const changes: { name?: string; isLab?: NoteCategoryLabs } = {};
      if (name !== current.name) changes.name = name;
      if (isLab !== current.isLab) changes.isLab = isLab;
      if (Object.keys(changes).length > 0) await updateNoteCategory(current.id, changes);
      setCategories((prev) => prev.map((c) => (c.id === current.id ? { ...c, name, isLab } : c)));
      toast.success('Category updated');
    } else {
      const saved = await createNoteCategory(name, isLab);
      if (saved.id) setCategories((prev) => [...prev, saved]);
      else void load(true);
      toast.success('Category created');
    }
    setCategoryModal(null);
  }

  async function handleDeleteCategory() {
    if (!deletingCategory) return;
    const id = deletingCategory.id;
    try {
      await deleteNoteCategory(id);
      setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, active: false } : c)));
      toast.success('Category deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The category could not be deleted.'));
    }
  }

  async function handleRestoreCategory(c: BoardCategory) {
    try {
      await restoreNoteCategory(c.id);
      setCategories((prev) => prev.map((x) => (x.id === c.id ? { ...x, active: true } : x)));
      toast.success(`${c.title} restored`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Restore failed'));
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const renderRecord = useCallback(
    (n: PatientNoteTemplate) => {
      const deleted = !n.active;
      return (
        <button
          type="button"
          // A deleted note is restored, not edited.
          disabled={deleted}
          onClick={() => setFormState({ note: n, category: null })}
          className="flex w-full items-center gap-3 rounded py-2 pr-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:hover:bg-transparent"
        >
          <span className="flex w-[38%] min-w-0 shrink-0 items-center gap-2">
            <span data-testid="note-name" className="truncate text-sm font-medium text-slate-700">
              {noteName(n)}
            </span>
            {inDeletedCategory(n) && (
              <OutlineBadge tone="orange" className="shrink-0">
                Category Deleted
              </OutlineBadge>
            )}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm text-slate-500" title={n.subject || undefined}>
            {n.subject || '—'}
          </span>
          <span className="flex w-8 shrink-0 justify-center">
            {n.hasContent && <FileText aria-label="Has content" className="h-4 w-4 text-purple-500" />}
          </span>
        </button>
      );
    },
    [inDeletedCategory],
  );

  function renderCategoryActions(c: BoardCategory) {
    if (c.deleted) return <AdminRowAction kind="restore" label={`Restore ${c.title}`} onClick={() => void handleRestoreCategory(c)} />;
    const count = liveIn(c.id);
    const category = categories.find((x) => x.id === c.id) ?? null;
    return (
      <>
        <AdminRowAction
          kind="add"
          title="Add note"
          label={`Add note to ${c.title}`}
          onClick={() => setFormState({ note: null, category: { id: c.id, name: c.title } })}
        />
        <AdminRowAction kind="edit" label={`Edit ${c.title}`} onClick={() => setCategoryModal({ current: category })} />
        <AdminRowAction
          kind="delete"
          label={`Delete ${c.title}`}
          blockedReason={count > 0 ? `Holds ${count} ${count === 1 ? 'note' : 'notes'} — move or delete them first` : null}
          onClick={() => (count > 0 ? toast.error(`Move or delete the notes in "${c.title}" before deleting it.`) : setDeletingCategory(c))}
        />
      </>
    );
  }

  function renderRecordActions(n: PatientNoteTemplate) {
    const name = noteName(n);
    if (!n.active) return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestoreNote(n)} />;
    return (
      <>
        <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setFormState({ note: n, category: null })} />
        <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeletingNote(n)} />
      </>
    );
  }

  const columnHeader = (
    <div className="flex items-center gap-3 pr-2">
      <span className="w-[38%] shrink-0">Name</span>
      <span className="min-w-0 flex-1">Subject</span>
      <span className="flex w-8 shrink-0 justify-center" title="Has content">
        <FileText className="h-3.5 w-3.5 text-slate-400" />
      </span>
    </div>
  );

  const editingCategory = formState?.note ? (categories.find((c) => c.id === formState.note?.categoryId) ?? null) : null;

  return (
    <AdminBoardPage
      title="Patient Notes"
      subtitle={locked ? SUBTITLE : `${SUBTITLE} ${DRAG_SUBTITLE}`}
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCategoryModal({ current: null })}>
          Add category
        </Button>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name or subject..."
          searchLabel="Search patient notes"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="patient-notes-show-deleted"
        />
      }
      hint={boardDragHint({ searching, filtering: false, showDeleted })}
      dialogs={
        <>
          {formState && (
            <PatientNoteFormModal
              key={formState.note ? `edit-${formState.note.id}` : `new-${formState.category?.id}`}
              note={formState.note}
              category={
                formState.note
                  ? editingCategory
                    ? { id: editingCategory.id, name: editingCategory.name }
                    : null
                  : formState.category
              }
              onClose={() => setFormState(null)}
              onSaved={handleNoteSaved}
            />
          )}
          {categoryModal && (
            <NoteCategoryModal
              current={categoryModal.current}
              categories={categories}
              onSave={handleCategorySave}
              onClose={() => setCategoryModal(null)}
            />
          )}
          <ConfirmDialog
            isOpen={deletingNote !== null}
            onClose={() => setDeletingNote(null)}
            onConfirm={handleDeleteNote}
            title={deletingNote ? `Delete "${noteName(deletingNote)}"?` : ''}
            message="It will no longer be offered when writing a note. You can restore it from Show deleted."
            confirmLabel="Delete note"
            variant="danger"
          />
          <ConfirmDialog
            isOpen={deletingCategory !== null}
            onClose={() => setDeletingCategory(null)}
            onConfirm={handleDeleteCategory}
            title={deletingCategory ? `Delete "${deletingCategory.title}"?` : ''}
            message="You can restore it from Show deleted."
            confirmLabel="Delete category"
            variant="danger"
          />
        </>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading patient notes…" />
      ) : searching && records.length === 0 ? (
        <AdminListEmpty title="No patient notes found" hint="Try adjusting your search." />
      ) : (
        <>
          {!searching && boardCategories.length === 0 && records.length === 0 && (
            <AdminListEmpty title="No categories yet" hint={locked ? undefined : 'Add a category, then add patient notes to it.'} />
          )}
          <CategoryBoard<PatientNoteTemplate>
            categories={boardCategories}
            records={records}
            getRecordId={getRecordId}
            getRecordTitle={getRecordTitle}
            getRecordCategoryId={getRecordCategoryId}
            renderRecord={renderRecord}
            columnHeader={columnHeader}
            renderCategoryActions={renderCategoryActions}
            renderRecordActions={renderRecordActions}
            isRecordDeleted={isDeleted}
            onReorderRecords={handleMove}
            onSaveError={handleSaveError}
            recordNoun={['note', 'notes']}
            dragDisabled={searching || showDeleted}
          />
        </>
      )}
    </AdminBoardPage>
  );
}
